import dayjs from 'dayjs';
import { store } from 'store';

import { deriveDuration, getFullDayString, isBeforeDay, timeValueFormat } from '~libs/dayjs';
import { WorkerAvailability } from '~weekly-planner/lib/common';

export const validateTasks: (values: any) => boolean = ({ task_ids = [] }) =>
  task_ids === null || task_ids?.length <= 45;

export const validateStartEndTimes: (values: any) => boolean = ({ start_time, end_time, ends_next_day = false }) => {
  if (start_time === undefined || start_time === '' || !dayjs(start_time, timeValueFormat).isValid()) {
    return false;
  }
  if (end_time === undefined || end_time === '' || !dayjs(end_time, timeValueFormat).isValid()) {
    return false;
  }
  return deriveDuration({ start_time, end_time, ends_next_day }) >= 0;
};

export const validateRepeatDates: (values: any) => boolean = ({ repeat, repeat_start, repeat_end }) => {
  if (!repeat) return true;
  if (repeat_start === undefined || repeat_start === '') return false;

  if (repeat_end && isBeforeDay(repeat_end, repeat_start)) {
    return false;
  }

  return true;
};

export const validateRepeatFrequency: (values: any) => boolean = ({ repeat, repeat_weekday, repeat_week_count }) => {
  if (!repeat) return true;
  if (!repeat_weekday || !repeat_week_count) return false;
  return true;
};

export const validateCareWorker: (values: any) => { valid: boolean; message?: string } = ({
  client_id,
  user_id = '',
}) => {
  if (!user_id) return { valid: true };

  const state = store.getState();
  const { clients } = state.main;
  const client = clients.rows.find(({ id }) => parseInt(client_id) === id);

  const { preferences } = client ?? {};
  const { no = [] } = preferences ?? {};
  if (no?.includes(parseInt(user_id))) {
    return {
      valid: false,
      message: 'Chosen care worker is not preferred by this client',
    };
  }

  return {
    valid: true,
  };
};

export const validateCancelReason: (values: any) => boolean = ({ cancellation_reason }) => {
  const state = store.getState();
  const {
    login: { settings },
  } = state.main;
  if (settings?.SYSTEM_CANCELLATION_MANDATORY_REASON === true && !cancellation_reason) return false;
  return true;
};

export const validateCancelReasonNote: (values: any) => boolean = ({ cancellation_reason_note }) => {
  const state = store.getState();
  const {
    login: { settings },
  } = state.main;
  if (settings?.SYSTEM_CANCELLATION_MANDATORY_REASON_NOTE === true && !cancellation_reason_note) return false;
  return true;
};

export const validateUserAvailability: (values: any, originalValues: any) => { valid: boolean; message?: string } = (
  changedValues,
  originalValues,
) => {
  const {
    date: oDate,
    repeat: oRepeat,
    repeat_start: oRepeatDate,
    start_time: oStartTime,
    end_time: oEndTime,
    user_id: oUserId,
  } = originalValues ?? {};
  const {
    date: cDate,
    repeat: cRepeat,
    repeat_start: cRepeatDate,
    start_time: cStartTime,
    end_time: cEndTime,
    user_id: cUserId,
  } = changedValues ?? {};

  const user_id = cUserId ?? oUserId;

  if (!user_id) return { valid: true };

  if (cDate || cRepeatDate || cStartTime || cEndTime) {
    const isRepeat = oRepeat || cRepeat;
    const date = isRepeat ? cRepeatDate || oRepeatDate : cDate || oDate;
    const start_time = cStartTime ?? oStartTime;
    const end_time = cEndTime ?? oEndTime;

    const state = store.getState();
    const { careWorkers } = state;
    const { users } = careWorkers;

    const day = getFullDayString(date) as keyof WorkerAvailability;
    const availability = users?.availability?.[day];
    const start = availability?.start;
    const end = availability?.end;

    if (!start || !end || start_time < start || end_time > end) {
      return {
        valid: false,
        message: 'Care Worker is not available at selected time.',
      };
    }
  }

  return {
    valid: true,
  };
};

export const validateUserQualifications: (values: any, originalValues: any) => { valid: boolean; message?: string } = (
  changedValues,
  originalValues,
) => {
  const { user_id: oUserId } = originalValues ?? {};
  const { qualification_level, service_type_id, user_id: cUserId } = changedValues ?? {};

  const user_id = cUserId ?? oUserId;
  if (!user_id) return { valid: true };
  if (service_type_id || qualification_level) {
    const state = store.getState();
    const { careWorkers } = state;
    const { users } = careWorkers;
    const userQualifications = users.rows.find(({ id }) => id === user_id)?.qualifications;

    const selected_qualifications = qualification_level ?? originalValues.qualification_level;

    const missingQualification = selected_qualifications.some((qualificaiton: string) => {
      return !userQualifications?.includes(qualificaiton);
    });

    if (missingQualification) {
      return {
        valid: false,
        message: 'Care Worker is not qualified for this service.',
      };
    }
  }

  return {
    valid: true,
  };
};

export const validatePackageServiceTypeCompatibility: (values: any) => boolean = ({
  service_type_id,
  package_fixed,
}) => {
  if (!service_type_id) return true;

  const state = store.getState();
  const service_types = state.appointments.serviceTypes.rows;

  const service_type_ids = service_types?.length
    ? service_types.map((row) => row.id)
    : package_fixed?.service_type_ids || [];

  if (!service_type_ids.includes(service_type_id)) return false;

  return true;
};
